import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeftIcon, Loader2Icon, SendIcon } from 'lucide-react'
import { useId, useState, type SyntheticEvent } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import type { ClientUrgency, RequestKind } from '@/api/types'
import { PortalAttachmentPicker } from '@/components/attachments/portal-attachment-picker'
import { FieldError, FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { MarkdownEditor } from '@/components/markdown-editor'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePendingAttachments } from '@/hooks/use-pending-attachments'
import { msg } from '@/i18n'
import { CLIENT_URGENCY_LABELS, CLIENT_URGENCY_ORDER } from '@/lib/labels'
import { requestUrlError } from '@/lib/request-url'
import { REQUEST_KIND_CLIENT_LABELS, REQUEST_KIND_ORDER } from '@/lib/requests'
import { matching, readSearch } from '@/lib/search-params'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/portal/requests/new')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch(
      {
        /** Lot 29 : le projet déjà choisi, quand on vient de sa page. */
        projet: matching(/^[0-9a-f]{24}$/),
      },
      search,
    ),
  component: NewRequestPage,
})

/** Aucun projet choisi : la valeur du sélecteur, jamais envoyée. */
const NO_PROJECT = 'aucun'

function NewRequestPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const titleId = useId()
  const descriptionId = useId()
  const urgencyId = useId()
  const kindId = useId()
  const projectId = useId()
  const urlId = useId()
  const { projet } = Route.useSearch()
  const t = msg().portalRequests.newRequest

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [urgency, setUrgency] = useState<ClientUrgency>('medium')
  const [kind, setKind] = useState<RequestKind>('change')
  const [project, setProject] = useState<string>(projet ?? NO_PROJECT)
  const [url, setUrl] = useState('')
  const [touched, setTouched] = useState(false)
  const pieces = usePendingAttachments()

  // Seuls les projets servis au portail se proposent : un autre rendrait 404.
  const projects = useQuery({
    queryKey: ['portal', 'projects'],
    queryFn: () => portalApi.projects(),
  })

  const create = useMutation({
    mutationFn: () =>
      portalApi.createTask({
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        urgency,
        request_kind: kind,
        ...(project === NO_PROJECT ? {} : { project_id: project }),
        ...(url.trim() ? { request_url: url.trim() } : {}),
        ...(pieces.ids.length > 0 ? { attachment_ids: pieces.ids } : {}),
      }),
    onError: (error) => {
      pieces.markRefused(error)
    },
    onSuccess: (task) => {
      pieces.clear()
      void queryClient.invalidateQueries({ queryKey: ['portal'] })
      toast.success(t.sent(task.key))
      void navigate({ to: '/portal/tasks/$taskKey', params: { taskKey: task.key } })
    },
  })

  const titleError = touched && !title.trim() ? t.titleRequired : undefined
  const urlError = touched ? requestUrlError(url) : undefined

  const submit = (event: SyntheticEvent) => {
    event.preventDefault()
    setTouched(true)
    if (!title.trim() || requestUrlError(url) || pieces.busy || pieces.blocked) return
    create.mutate()
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={
          <Button asChild variant="ghost" size="sm">
            <Link to="/portal">
              <ArrowLeftIcon />
              {t.back}
            </Link>
          </Button>
        }
        title={t.title}
      />

      <Frame>
        <form onSubmit={submit} noValidate className="max-w-2xl space-y-4">
          <FormError>{errorText(create.error)}</FormError>

          <fieldset className="space-y-2">
            <legend id={kindId} className="text-sm font-medium">
              {t.kindLegend}
            </legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="nature-demande">
              {REQUEST_KIND_ORDER.map((value) => (
                <label
                  key={value}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm',
                    kind === value && 'border-primary ring-1 ring-primary',
                  )}
                >
                  <input
                    type="radio"
                    name={kindId}
                    value={value}
                    checked={kind === value}
                    onChange={() => {
                      setKind(value)
                    }}
                  />
                  {REQUEST_KIND_CLIENT_LABELS[value]}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor={titleId}>{t.titleLabel}</Label>
            <Input
              id={titleId}
              value={title}
              placeholder={t.titlePlaceholder}
              aria-invalid={!!titleError}
              aria-describedby={titleError ? `${titleId}-error` : undefined}
              onChange={(event) => {
                setTitle(event.target.value)
              }}
            />
            <FieldError id={`${titleId}-error`}>{titleError}</FieldError>
          </div>

          <div className="space-y-2">
            <Label htmlFor={descriptionId}>{t.description}</Label>
            <div onPaste={pieces.onPaste}>
              <MarkdownEditor
                id={descriptionId}
                value={description}
                onChange={setDescription}
                rows={6}
                placeholder={t.descriptionPlaceholder}
              />
            </div>
            <PortalAttachmentPicker pending={pieces} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={projectId}>{t.project}</Label>
              <Select value={project} onValueChange={setProject}>
                <SelectTrigger id={projectId} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PROJECT}>{t.noProject}</SelectItem>
                  {(projects.data ?? []).map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor={urlId}>{t.url}</Label>
              <Input
                id={urlId}
                type="url"
                inputMode="url"
                value={url}
                placeholder="https://…"
                aria-invalid={!!urlError}
                aria-describedby={urlError ? `${urlId}-error` : undefined}
                onChange={(event) => {
                  setUrl(event.target.value)
                }}
              />
              <FieldError id={`${urlId}-error`}>{urlError}</FieldError>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={urgencyId}>{t.urgency}</Label>
            <Select
              value={urgency}
              onValueChange={(value) => {
                setUrgency(value as ClientUrgency)
              }}
            >
              <SelectTrigger id={urgencyId} className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLIENT_URGENCY_ORDER.map((value) => (
                  <SelectItem key={value} value={value}>
                    {CLIENT_URGENCY_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button asChild variant="outline" type="button">
              <Link to="/portal">{t.cancel}</Link>
            </Button>
            <Button type="submit" disabled={create.isPending || pieces.busy || pieces.blocked}>
              {create.isPending ? <Loader2Icon className="animate-spin" /> : <SendIcon />}
              {t.submit}
            </Button>
          </div>
        </form>
      </Frame>
    </div>
  )
}

/**
 * Le quota d'écriture du portail existe contre un mot de passe volé, pas
 * contre un client bavard : quelqu'un qui le rencontre a de bonnes raisons
 * d'écrire, et « Trop de tentatives » le traiterait en suspect. On dit ce qui
 * se passe et ce qu'il peut faire — son interlocuteur reste joignable.
 */
function errorText(error: unknown): string | null {
  const t = msg().portalRequests.newRequest
  if (!(error instanceof ApiError)) return error ? t.failed : null
  if (error.reason === 'attachment_invalid') return t.attachmentInvalid
  if (error.reason === 'portal_write_quota') return t.quota
  return error.message
}
